/**
 * Parameterized measures — the part of a stored policy that carries a number.
 *
 * ## The problem this solves
 *
 * The analytics registry is a list of literal measures: `failing_courses`,
 * `tardy_count`, `below_b_courses`. That is fine while the thresholds are the
 * app's own, and useless the moment a policy owns them. "No more than 1
 * unexcused absence per 2 weeks" is not a measure we can enumerate; it is a
 * *family* — count attendance events, of this kind, with this excused status,
 * in this window — with the parameters supplied by a document.
 *
 * So a family is a factory, and its output is identified by a parseable id:
 *
 *     attendance_count:kind=absent;n=14;scope=daily;status=unexcused;window=calendar_days
 *
 * ## The property that keeps this cheap
 *
 * **The parameter space is closed by configuration.** The only parameter values
 * that exist anywhere are the ones some saved program or column selection
 * names. So the set of measure ids in play is finite and knowable before any
 * student is read, `projectStudent()` can still precompute a fixed
 * `Record<string, number | null>`, and the `AnalysisRecord` seam survives — as
 * does the unbuilt `CohortSnapshot`, since a snapshot builder reads the same
 * configuration. Let the parameters be arbitrary at read time instead and every
 * measure has to be recomputed from `StudentDoc` at render, which dissolves the
 * seam. It is worth the extra argument.
 *
 * ## Everything here is computed from the student document
 *
 * `recentGrades` and `recentAttendance` are already on the doc the dashboard
 * loads, so these families add **zero reads**. The only external inputs are the
 * school-day list and the term lookup, both single `lookups/*` documents the
 * app loads once.
 *
 * ## Two traps, both load-bearing
 *
 * 1. **`recentAttendance` is daily attendance, and the array could stop being
 *    only that.** The importer is built to write both `daily_attendance.csv`
 *    rows (no `period`) and `section_attendance.csv` rows (with `period`) into
 *    the same array. **Section attendance is not currently imported** — the
 *    data is unreliably reported, so the school does not send it — and every
 *    record in `recentAttendance` today is a daily one.
 *
 *    `scope` exists anyway, defaulting to `daily`, because the day that file
 *    starts arriving a "2 tardies per 2 weeks" rule would begin counting one
 *    late morning as four or five events and nothing would announce it. A
 *    policy criterion is exactly the wrong place to discover that. Costing one
 *    parameter and one predicate, the guard is worth having before the data is.
 *
 *    **If section attendance is ever turned on, it should land in its own array
 *    rather than this one** — `classAbsences`, or similar. Two kinds of record
 *    in one field is what made the guard necessary; a separate field makes the
 *    distinction unmissable instead of merely documented, and per-class
 *    attendance wants its own questions anyway ("this student has missed four
 *    of this class"). It also needs resolution work this codebase cannot do
 *    yet: a teacher who never takes attendance produces no absence record on a
 *    day the student was absent, and Updraft does not know which classes met on
 *    a given day, so a missing record and a present student are currently
 *    indistinguishable.
 * 2. **Missing student data is not the same as an unanswerable question.** This
 *    is the line every family here is drawn on, and it is worth stating plainly
 *    because the obvious implementation gets it backwards.
 *
 *    These measures are **searches for disqualifiers**, not measurements. "No
 *    unexcused absence in the last two weeks" is answered by looking for
 *    absences; finding none is a complete and correct answer, whether the
 *    student has a spotless record or the attendance file has not landed. So a
 *    student with no data counts **zero**, and the criterion passes. We are
 *    watching for bad news arriving — a teacher entering a C-, an office
 *    entering a tardy — not for the presence of good news.
 *
 *    An **unresolvable window** is a different thing entirely and still returns
 *    `null`. A term code that does not join, a school-day window with no school
 *    days loaded: there the question itself could not be asked, which is a
 *    configuration fault rather than a fact about the student, and silently
 *    treating it as "no disqualifiers" would hide a broken policy.
 *
 *    So: **absence of student data → no disqualifier. Absence of the ability to
 *    ask → unknown.** The protection against a blanked import reinstating an
 *    entire cohort at once does not live here; it lives in the evaluator's
 *    blast-radius circuit breaker, which is the right layer for it, because it
 *    can see that four hundred students changed at once and one criterion
 *    cannot.
 */
import { attendanceStatus, type AttendanceStatus } from "./attendanceStatus.js";
import { termWindow, type TermLookup } from "./terms.js";
import type { AttendanceRecord } from "./attendance.js";
import type { CriterionEvidence } from "./programs.js";
import type { GradeRecord, TranscriptEntry, TranscriptTerm } from "./grades.js";
import { transcriptGradeValue } from "./gradeScale.js";

/** The subset of `StudentDoc` these families read. */
export interface ProgramMeasureSubject {
  recentGrades?: GradeRecord[];
  recentAttendance?: AttendanceRecord[];
  transcript?: TranscriptEntry[];
}

/**
 * Everything a family needs that is not on the student document.
 *
 * `schoolDays` is ISO dates in ascending order, past days only — the same list
 * `lookups/school_days` publishes. `terms` is the `lookups/marking_periods`
 * rollup. Both are optional, and a window needing an absent one resolves to
 * `undefined` rather than guessing.
 */
export interface ProgramMeasureContext {
  today: string;
  schoolDays?: readonly string[];
  terms?: TermLookup;
  /**
   * `lookups/sections`, for the department a grade belongs to.
   *
   * Needed only by a measure narrowed to departments ("core academic classes
   * only"). A grade record carries its section id and not its department, and
   * the section lookup is one document both callers already load, so the join
   * happens here rather than by widening every grade the importer writes.
   * Absent while such a measure is in play is `unknown`, never "every course".
   */
  sections?: Readonly<Record<string, { department?: string } | undefined>>;
  /** Narrows term resolution when two schools run terms with the same code. */
  schoolYear?: string;
  schoolId?: string;
}

/** The closed window vocabulary. Adding a kind is a deliberate act, not a config value. */
export type MeasureWindow =
  | { kind: "ytd" }
  | { kind: "calendar_days"; n: number }
  | { kind: "school_days"; n: number }
  | { kind: "term"; term: string };

/** A resolved window as an inclusive date range. `start` absent means "no lower bound". */
export interface DateRange {
  start?: string;
  end: string;
}

export type AttendanceKind = "absent" | "tardy";

/**
 * Which records count.
 *
 * `daily` is the default and is what a policy means: one row per student per
 * day, from `daily_attendance.csv`. `period` counts section-level rows, which
 * answers a different and legitimate question ("how many classes did they
 * miss") — but never the one a "2 tardies" rule is asking.
 *
 * `period` and `any` select nothing today: section attendance is not imported.
 * They are the guard described at the top of this file, not a live feature.
 */
export type AttendanceScope = "daily" | "period" | "any";

/**
 * Excused filter. `any` counts everything.
 *
 * There is deliberately no "unexcused or unknown" option. A blank code is the
 * absence of an assertion, not an assertion of fault, and a policy that lumped
 * the two would punish a student for an import gap — see `attendanceStatus.ts`.
 * A policy wanting that behavior has to say so with two criteria.
 */
export type AttendanceStatusFilter = "any" | AttendanceStatus;

export interface GradeCountParams {
  threshold: number;
  /**
   * Only courses in these departments, as `lookups/sections` spells them.
   * Absent means every course.
   *
   * An include-list rather than an exclude-list because that is how the
   * policies are written — "core academic classes only" — and because the
   * author picks from the departments that exist. A department that appears
   * next year is left out until somebody decides it counts, which is a
   * question for a person rather than a default.
   */
  departments?: string[];
}

export interface TranscriptCountParams {
  /** On the transcript scale, where a D+ is 1.6 and a C- is 1.8. */
  threshold: number;
  /** `FY`, `S1` or `S2`. */
  term: TranscriptTerm;
  /** Defaults to the context's school year, so a policy need not restate it. */
  year?: string;
}

export interface AttendanceCountParams {
  kind: AttendanceKind;
  scope: AttendanceScope;
  status: AttendanceStatusFilter;
  window: MeasureWindow;
}

export type ProgramMeasure =
  | { family: "grades_below"; params: GradeCountParams }
  | { family: "transcript_below"; params: TranscriptCountParams }
  | { family: "attendance_count"; params: AttendanceCountParams };

/**
 * The families a policy may name.
 *
 * There was a `grades_above` too — "count courses above a grade" — and nothing
 * ever wanted it. A qualifying policy asks whether anything is *wrong*: "no
 * courses below a B-" is `grades_below <= 0`, and the disqualifier form covers
 * every case anybody has described. It also inverted the safety direction,
 * since with a `>=` comparison a student with no data read as failing rather
 * than passing, which is the opposite of every other family here.
 *
 * If a minimum ever genuinely appears — "at least four courses above passing",
 * the MIAA shape — it is a few lines to add back, deliberately rather than by
 * having been left lying around.
 */
export const PROGRAM_MEASURE_FAMILIES = [
  "grades_below",
  "transcript_below",
  "attendance_count",
] as const;

/* -------------------------------------------------------------------------- */
/* Ids                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * A department name, made safe for the id's own punctuation.
 *
 * Only the four characters the id uses as separators are escaped, so the ids
 * that actually occur — `dept=English,Math,Science,Social Studies` — stay
 * readable in the stored policy, where somebody will eventually read one while
 * working out why a student's status changed.
 */
function encodeDepartment(name: string): string {
  return name.replace(
    /[%;,=]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`,
  );
}

function decodeDepartment(encoded: string): string {
  return encoded.replace(/%([0-9A-F]{2})/g, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
}

/** Sorted and de-duplicated, so two spellings of one set share an id. */
function canonicalDepartments(departments: readonly string[] | undefined): string[] {
  return [...new Set((departments ?? []).map((d) => d.trim()).filter(Boolean))].sort();
}

function departmentField(
  departments: readonly string[] | undefined,
): Record<string, string> {
  const list = canonicalDepartments(departments);
  return list.length ? { dept: list.map(encodeDepartment).join(",") } : {};
}

function parseDepartments(value: string | undefined): string[] | null | undefined {
  if (value === undefined) return undefined;
  const list = value.split(",").map(decodeDepartment);
  if (list.some((d) => !d.trim())) return null;
  return canonicalDepartments(list);
}

function encodeParams(params: Record<string, string | number>): string {
  return Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join(";");
}

/**
 * Canonical id for a measure. Parameters are sorted, so two policies asking
 * the same question produce the same id and share one computed column.
 */
export function programMeasureId(measure: ProgramMeasure): string {
  if (measure.family === "attendance_count") {
    const { kind, scope, status, window } = measure.params;
    const fields: Record<string, string | number> = {
      kind,
      scope,
      status,
      window: window.kind,
    };
    if (window.kind === "calendar_days" || window.kind === "school_days")
      fields.n = window.n;
    if (window.kind === "term") fields.term = window.term;
    return `attendance_count:${encodeParams(fields)}`;
  }
  if (measure.family === "transcript_below") {
    const { threshold, term, year } = measure.params;
    return `transcript_below:${encodeParams(
      year ? { term, threshold, year } : { term, threshold },
    )}`;
  }
  const { threshold, departments } = measure.params;
  return `grades_below:${encodeParams({
    threshold,
    ...departmentField(departments),
  })}`;
}

function parseParams(rest: string): Record<string, string> | null {
  const out: Record<string, string> = {};
  if (rest === "") return out;
  for (const part of rest.split(";")) {
    const eq = part.indexOf("=");
    if (eq <= 0) return null;
    const key = part.slice(0, eq);
    if (key in out) return null;
    out[key] = part.slice(eq + 1);
  }
  return out;
}

function parseWindow(params: Record<string, string>): MeasureWindow | null {
  switch (params.window) {
    case "ytd":
      return "n" in params || "term" in params ? null : { kind: "ytd" };
    case "calendar_days":
    case "school_days": {
      const n = Number(params.n);
      if (!Number.isInteger(n) || n <= 0) return null;
      return { kind: params.window, n };
    }
    case "term":
      return params.term ? { kind: "term", term: params.term } : null;
    default:
      return null;
  }
}

function isOneOf<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
): value is T {
  return allowed.includes(value as T);
}

/**
 * Parses a measure id, or returns `null` when it is not a well-formed family id.
 *
 * Strict by design: an unrecognized parameter, a missing one or a duplicate is
 * a `null`, not a default. A policy document is hand-edited, and a typo that
 * quietly became "count all tardies ever" would be invisible and wrong in the
 * permissive direction.
 */
export function parseProgramMeasureId(id: string): ProgramMeasure | null {
  const colon = id.indexOf(":");
  if (colon <= 0) return null;
  const family = id.slice(0, colon);
  const params = parseParams(id.slice(colon + 1));
  if (!params) return null;

  if (family === "grades_below") {
    const allowed = new Set(["threshold", "dept"]);
    if (Object.keys(params).some((key) => !allowed.has(key))) return null;
    if (params.threshold === undefined) return null;
    const threshold = Number(params.threshold);
    if (!Number.isFinite(threshold)) return null;
    const departments = parseDepartments(params.dept);
    if (departments === null) return null;
    return {
      family,
      params: { threshold, ...(departments?.length ? { departments } : {}) },
    };
  }

  if (family === "transcript_below") {
    const threshold = Number(params.threshold);
    if (!Number.isFinite(threshold)) return null;
    if (!isOneOf(params.term, ["FY", "S1", "S2"] as const)) return null;
    const allowed = new Set(["threshold", "term", "year"]);
    if (Object.keys(params).some((key) => !allowed.has(key))) return null;
    return {
      family,
      params: {
        threshold,
        term: params.term,
        ...(params.year ? { year: params.year } : {}),
      },
    };
  }

  if (family === "attendance_count") {
    const { kind, scope, status } = params;
    if (!isOneOf(kind, ["absent", "tardy"] as const)) return null;
    if (!isOneOf(scope, ["daily", "period", "any"] as const)) return null;
    if (!isOneOf(status, ["any", "excused", "unexcused", "unknown"] as const))
      return null;
    const window = parseWindow(params);
    if (!window) return null;
    const expected = new Set(["kind", "scope", "status", "window"]);
    if (window.kind === "calendar_days" || window.kind === "school_days")
      expected.add("n");
    if (window.kind === "term") expected.add("term");
    if (Object.keys(params).some((key) => !expected.has(key))) return null;
    return {
      family: "attendance_count",
      params: { kind, scope, status, window },
    };
  }

  return null;
}

/** True when an id names a parameterized family rather than a registry literal. */
export function isProgramMeasureId(id: string): boolean {
  return parseProgramMeasureId(id) !== null;
}

/* -------------------------------------------------------------------------- */
/* Windows                                                                    */
/* -------------------------------------------------------------------------- */

/** ISO date arithmetic in UTC, so a DST boundary cannot shift the answer a day. */
export function addDays(date: string, delta: number): string {
  const ms = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(ms)) return date;
  return new Date(ms + delta * 86_400_000).toISOString().slice(0, 10);
}

/**
 * Resolves a window to a date range, or `undefined` when it cannot be resolved.
 *
 * A `school_days` window with fewer than `n` days elapsed is **not** unknown —
 * "the last 10 school days" in the second week of September honestly means
 * "all of them so far". Missing the school-day list entirely is unknown, and so
 * is a term code that does not join.
 */
export function resolveWindow(
  window: MeasureWindow,
  ctx: ProgramMeasureContext,
): DateRange | undefined {
  switch (window.kind) {
    case "ytd":
      return { end: ctx.today };
    case "calendar_days":
      // Inclusive of today, so `n=14` is a fortnight and not fifteen days.
      return { start: addDays(ctx.today, -(window.n - 1)), end: ctx.today };
    case "school_days": {
      if (!ctx.schoolDays?.length) return undefined;
      const elapsed = ctx.schoolDays.filter((day) => day <= ctx.today);
      if (!elapsed.length) return undefined;
      const start = elapsed[Math.max(0, elapsed.length - window.n)];
      return { start, end: ctx.today };
    }
    case "term": {
      if (!ctx.terms) return undefined;
      const span = termWindow(ctx.terms, window.term, {
        schoolYear: ctx.schoolYear,
        schoolId: ctx.schoolId,
      });
      if (!span) return undefined;
      // Never count past today: a term still running would otherwise look like
      // a completed one with a suspiciously clean record at its far end.
      return {
        start: span.start,
        end: span.end < ctx.today ? span.end : ctx.today,
      };
    }
  }
}

function inRange(date: string, range: DateRange): boolean {
  if (range.start && date < range.start) return false;
  return date <= range.end;
}

/* -------------------------------------------------------------------------- */
/* The families                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The winning grade record per course.
 *
 * Mirrors the rule `computeSectionCurrentGrade` applies in the webapp: group by
 * section, take the most recent dated record. Restated here rather than
 * imported because `shared/` cannot reach into `webapp/`; the webapp's
 * `project.ts` is the place that must not drift from it.
 *
 * Returns the records rather than bare numbers so a caller can name the course
 * a grade belongs to — "two courses below a B-" is not an answer anybody can
 * act on until it says which two.
 */
export function currentCourseGradeRecords(
  subject: ProgramMeasureSubject,
): GradeRecord[] {
  const bySection = new Map<string, GradeRecord>();
  for (const grade of subject.recentGrades ?? []) {
    if (grade.is_real_course === false || !Number.isFinite(grade.grade))
      continue;
    const held = bySection.get(grade.section_id);
    if (!held || grade.date.localeCompare(held.date) > 0)
      bySection.set(grade.section_id, grade);
  }
  return [...bySection.values()];
}

/** Just the numbers, for callers that do not care which course. */
export function currentCourseGrades(subject: ProgramMeasureSubject): number[] {
  return currentCourseGradeRecords(subject).map((grade) => grade.grade);
}

/**
 * The department a section belongs to, or null when it has none.
 *
 * Aspen writes the literal string "N/A" for advisories, guided study and
 * enrichment, and that is not a department anybody could list — so it reads as
 * none, as it does in the webapp's `dept_grade` measures.
 */
export function sectionDepartment(
  sectionId: string,
  ctx: Pick<ProgramMeasureContext, "sections">,
): string | null {
  const department = ctx.sections?.[sectionId]?.department?.trim();
  if (!department || department === "N/A") return null;
  return department;
}

/**
 * Current course grades, narrowed to a set of departments.
 *
 * `null` when a narrowing was asked for and the section lookup is not there to
 * answer it. That is the "could not ask" case, and the tempting fallback —
 * count every course — is wrong in a way nobody would see: a Spanish grade the
 * policy excludes would start deciding a middle schooler's eligibility.
 *
 * A section missing from a lookup that *is* loaded is simply not in any listed
 * department. The lookup is rebuilt from the same import as the grades, so a
 * miss is a retired section rather than a gap in the join.
 */
function coursesInDepartments(
  subject: ProgramMeasureSubject,
  departments: readonly string[] | undefined,
  ctx: ProgramMeasureContext,
): GradeRecord[] | null {
  const records = currentCourseGradeRecords(subject);
  if (!departments?.length) return records;
  if (!ctx.sections) return null;
  const wanted = new Set(departments);
  return records.filter((record) => {
    const department = sectionDepartment(record.section_id, ctx);
    return department !== null && wanted.has(department);
  });
}

export function matchesScope(
  record: AttendanceRecord,
  scope: AttendanceScope,
): boolean {
  if (scope === "any") return true;
  const isPeriodRecord = record.period !== undefined && record.period !== "";
  return scope === "period" ? isPeriodRecord : !isPeriodRecord;
}

/**
 * Counts finalized transcript grades below a floor, for one term.
 *
 * ## "No grade from that term is failing"
 *
 * That sentence is the whole rule, and it is deliberately vacuous when the
 * transcript is empty: **if no grade exists, no grade is failing.** A student
 * whose semester 1 grades have not been published yet has not failed semester
 * 1; they have an unpublished transcript. So this counts zero and the criterion
 * passes, and it starts failing the moment a real failing grade lands.
 *
 * This matters more than it looks, because semester 2 does not begin when
 * semester 1 ends. Teachers get time to finalize, so for the first stretch of
 * S2 the transcript is legitimately silent about S1. A rule that treated
 * silence as "unknown" would flip every senior to a non-answer in January for
 * reasons no student could act on.
 *
 * The gap it leaves is real and small: at the very start of S2 a student who
 * did badly in S1 keeps the privilege until their transcript catches up. In
 * practice the current-grade criterion covers much of it, because in the first
 * days of S2 `recentGrades` may still hold S1's grades — and a student who was
 * struggling then is not usually thriving on day three of the new term.
 *
 * ## Non-numeric grades fall out, they do not count as zero
 *
 * `transcriptGradeValue` returns null for `Pass`, `CR`, `W` and anything
 * unrecognized. Those are courses with no numeric outcome, not failures, and
 * treating a `Pass` as a 0.0 would fail a student for taking a pass/fail
 * course. `INC` and `ANC` *are* in the table at 0.0, because those are real
 * failures the school marks as earning no credit.
 */
export function transcriptBelowDetail(
  subject: ProgramMeasureSubject,
  params: TranscriptCountParams,
  ctx: ProgramMeasureContext,
): ProgramMeasureResult {
  const year = params.year ?? ctx.schoolYear;
  const evidence: CriterionEvidence[] = [];
  for (const entry of subject.transcript ?? []) {
    if (entry.term !== params.term) continue;
    if (year && entry.school_year !== year) continue;
    const value = transcriptGradeValue(entry.grade);
    if (value === null) continue;
    if (value < params.threshold)
      // The letter is what the transcript shows and what the student remembers,
      // so it goes in the label; the number is what the threshold compared.
      evidence.push({
        label: `${entry.course_name} (${entry.grade})`,
        value,
      });
  }
  return { value: evidence.length, evidence };
}

export function transcriptBelowCount(
  subject: ProgramMeasureSubject,
  params: TranscriptCountParams,
  ctx: ProgramMeasureContext,
): number {
  return transcriptBelowDetail(subject, params, ctx).value ?? 0;
}

/** Counts attendance events matching the family's parameters within its window. */
export function attendanceCountDetail(
  subject: ProgramMeasureSubject,
  params: AttendanceCountParams,
  ctx: ProgramMeasureContext,
): ProgramMeasureResult {
  const range = resolveWindow(params.window, ctx);
  if (!range) return { value: null, evidence: [] };

  const noun = params.kind === "absent" ? "Absent" : "Tardy";
  const evidence: CriterionEvidence[] = [];
  for (const record of subject.recentAttendance ?? []) {
    const flagged = params.kind === "absent" ? record.absent : record.tardy;
    // Aspen's daily export sets exactly one of the two booleans; an absence is
    // not also a tardy, so the two kinds never double-count each other.
    if (!flagged) continue;
    if (!matchesScope(record, params.scope)) continue;
    if (!inRange(record.date, range)) continue;
    if (
      params.status !== "any" &&
      attendanceStatus(record.code) !== params.status
    )
      continue;
    // The raw code travels with the date. It is exactly what the office edits
    // when a student turns up with a note, and seeing "L" rather than "L-Ex" is
    // what makes that conversation possible at the desk.
    evidence.push({
      label: record.code ? `${noun} (${record.code})` : noun,
      date: record.date,
    });
  }
  // Most recent first: a note is nearly always about the last few days.
  evidence.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  return { value: evidence.length, evidence };
}

export function attendanceCount(
  subject: ProgramMeasureSubject,
  params: AttendanceCountParams,
  ctx: ProgramMeasureContext,
): number | null {
  return attendanceCountDetail(subject, params, ctx).value;
}

/**
 * A measure's number plus what made it up.
 *
 * `value` is `null` when the question could not be asked at all — a window
 * that did not resolve, a department filter with no section lookup. It never
 * means "this student has no data"; see the note at the top of this file.
 */
export interface ProgramMeasureResult {
  value: number | null;
  evidence: CriterionEvidence[];
}

function gradeEvidence(record: GradeRecord): CriterionEvidence {
  return {
    label: record.course_name ?? record.section_id,
    value: record.grade,
    // An id, not the teacher's name: the name is a join on `lookups/sections`
    // at read time, which every staff member can already read, and it stays
    // right when a section changes hands mid-year.
    section_id: record.section_id,
  };
}

/**
 * A measure's number **and the items behind it**.
 *
 * The evidence is the whole reason the status document is worth writing: "two
 * courses below a B-" is the office's answer, and "which two" is the student's
 * immediate next question. For attendance it is stronger than that — the date
 * is actionable, because a student turning up with a note for the 18th can have
 * that tardy excused, and only a dated list tells the office which day to look
 * at.
 */
export function programMeasureDetail(
  measure: ProgramMeasure,
  subject: ProgramMeasureSubject,
  ctx: ProgramMeasureContext,
): ProgramMeasureResult {
  switch (measure.family) {
    case "grades_below": {
      const { threshold, departments } = measure.params;
      const courses = coursesInDepartments(subject, departments, ctx);
      if (!courses) return { value: null, evidence: [] };
      const matching = courses.filter((record) => record.grade < threshold);
      return { value: matching.length, evidence: matching.map(gradeEvidence) };
    }
    case "transcript_below":
      return transcriptBelowDetail(subject, measure.params, ctx);
    case "attendance_count":
      return attendanceCountDetail(subject, measure.params, ctx);
  }
}

export function programMeasureValue(
  measure: ProgramMeasure,
  subject: ProgramMeasureSubject,
  ctx: ProgramMeasureContext,
): number | null {
  // One implementation of each family, so a column showing the number cannot
  // disagree with the verdict built from it. No grades means no grades below
  // the floor: a student the gradebook has not reached has not failed anything.
  return programMeasureDetail(measure, subject, ctx).value;
}

/** Convenience: parse an id and compute it. `null` covers both "bad id" and "no data". */
export function resolveProgramMeasure(
  id: string,
  subject: ProgramMeasureSubject,
  ctx: ProgramMeasureContext,
): number | null {
  const measure = parseProgramMeasureId(id);
  return measure ? programMeasureValue(measure, subject, ctx) : null;
}

/* -------------------------------------------------------------------------- */
/* Prose                                                                      */
/* -------------------------------------------------------------------------- */

const WINDOW_LABELS: Record<
  MeasureWindow["kind"],
  (w: MeasureWindow) => string
> = {
  ytd: () => "this year",
  calendar_days: (w) => `in the last ${(w as { n: number }).n} days`,
  school_days: (w) => `in the last ${(w as { n: number }).n} school days`,
  term: (w) => `in ${(w as { term: string }).term}`,
};

/** A window's prose, for a detail line beside an authored label. */
export function describeWindow(window: MeasureWindow): string {
  return WINDOW_LABELS[window.kind](window);
}

/**
 * A fallback label for a family measure, used by pickers and by nothing that
 * a student reads.
 *
 * Criteria carry an authored `label` precisely so nobody is handed generated
 * prose; this exists for the column menu, where "Unexcused absences in the last
 * 14 days" is exactly right and no human should have to type it.
 */
export function describeProgramMeasure(measure: ProgramMeasure): string {
  if (measure.family === "attendance_count") {
    const { kind, scope, status, window } = measure.params;
    const qualifier = status === "any" ? "" : `${status} `;
    const noun = kind === "absent" ? "absences" : "tardies";
    const unit = scope === "period" ? " (per class)" : "";
    const head = `${qualifier}${noun}`;
    return `${head.charAt(0).toUpperCase()}${head.slice(1)}${unit} ${describeWindow(window)}`;
  }
  if (measure.family === "transcript_below") {
    const { term, threshold } = measure.params;
    return `${term} transcript grades below ${threshold.toFixed(1)}`;
  }
  const { threshold, departments } = measure.params;
  return `${departments?.length ? "Classes" : "Courses"} below ${threshold.toFixed(1)}${inDepartmentsPhrase(departments)}`;
}

/** " in English, Math and Science", or nothing for every course. */
function inDepartmentsPhrase(departments: readonly string[] | undefined): string {
  const list = canonicalDepartments(departments);
  if (!list.length) return "";
  if (list.length === 1) return ` in ${list[0]}`;
  return ` in ${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}

/** A measure's value as a phrase: "2 courses below 2.7", "1 class below 1.8 in …". */
export function describeProgramMeasureValue(
  measure: ProgramMeasure,
  value: number,
): string {
  const count = Number.isInteger(value) ? `${value}` : value.toFixed(1);
  const phrase = lowerFirst(describeProgramMeasure(measure));
  // "1 class below 1.8", not "1 classes". Only the grade families lead with
  // their noun; attendance leads with a qualifier ("unexcused absences").
  if (value === 1 && measure.family === "grades_below")
    return `${count} ${phrase.replace(/^(class|course)(es|s)\b/, "$1")}`;
  return `${count} ${phrase}`;
}

/**
 * The description as it reads mid-sentence: "courses below 2.7".
 *
 * Only the first letter. Lowercasing the whole phrase turned "in English and
 * Social Studies" into "in english and social studies", which is a department
 * nobody has.
 */
export function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** Whether a measure joins against `lookups/sections`, so a caller can load it. */
export function programMeasureNeedsSections(measure: ProgramMeasure): boolean {
  return (
    measure.family === "grades_below" &&
    Boolean(measure.params.departments?.length)
  );
}
